// Copyright 2021 Sorama B.V.

import { Point3D, Region } from "datatypes/SoramaLocalApi";
import { WebCamDeviceProperties, WebcamImageOrientation } from "datatypes/SoramaWebApi";
import { RegionBasedVideoCalibration } from "./RegionBasedVideoCalibration";

export class WebcamDevicePropertiesCalibration extends RegionBasedVideoCalibration {
    constructor(
        position: Point3D,
        resolution: { width: number; height: number },
        distance: number,
        properties: WebCamDeviceProperties
    ) {
        super(
            resolution,
            distance,
            WebcamDevicePropertiesCalibration.computeVisibleRegion(
                distance,
                position,
                resolution.width / resolution.height,
                properties
            )
        );
    }

    private static computeVisibleRegion(
        distance: number,
        position: Point3D,
        aspectRatio: number,
        properties: WebCamDeviceProperties
    ): Region {
        // The stored video is already stored and corrected (e.g. rotation). Determine the new field of view
        let tanFov: number;
        distance = distance - position.z;
        const fovVertical = Math.atan2(1.0, properties.focalX) * 2;

        switch (properties.imageOrientation) {
            case WebcamImageOrientation.Rotate90:
            case WebcamImageOrientation.Rotate270:
                tanFov =
                    (2 * distance * (1 - properties.sensorOffsetCorrectionY / 1000) * Math.tan(fovVertical / 2)) /
                    aspectRatio;
                break;
            case WebcamImageOrientation.Rotate180:
            default:
                // Normal orientation
                tanFov = 2 * distance * (1 - properties.sensorOffsetCorrectionY / 1000) * Math.tan(fovVertical / 2);
                break;
        }

        // Map it on resolution video
        const croppedWidth = tanFov * aspectRatio;
        const croppedHeight = tanFov;

        return {
            width: croppedWidth,
            height: croppedHeight,
            centerX: position.x,
            centerY: position.y,
        };
    }
}
